import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import TextField from "@mui/material/TextField";

import { API_URL } from "config";
import "./ProjectLiveChatPanel.css";

const MAX_BODY_LENGTH = 4000;
const MAX_GAP_PAGES_PER_REFRESH = 3;
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;

// Autocomplete measures its own width; this popper keeps that width while
// sitting above the fixed chat panel instead of behind it.
const MentionPopper = React.forwardRef(function MentionPopper(props, ref) {
  return (
    <Popper
      {...props}
      ref={ref}
      placement="top-start"
      style={{ ...props.style, zIndex: 1600 }}
    />
  );
});

MentionPopper.propTypes = {
  style: PropTypes.object,
};

// New live-chat rows are plain text and React renders this value as text, not
// HTML. Keep literal characters such as <3, code, and <script> visible.
const safeText = (value) => String(value ?? "");
const documentIsHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
};

const initials = (name) =>
  String(name || "U")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const mergeMessages = (...groups) => {
  const byId = new Map();
  groups.flat().forEach((message) => {
    if (message?.id === undefined || message?.id === null) return;
    byId.set(String(message.id), message);
  });

  return Array.from(byId.values()).sort((left, right) => Number(left.id) - Number(right.id));
};

const messageForResponse = (response, payload, fallback) => {
  if (response?.status === 401) return "Your session has ended. Please sign in again.";
  if (response?.status === 403) return "You no longer have access to this project chat.";
  return payload?.message || fallback;
};

const createChatRequestError = (message, status) => {
  const error = new Error(message);
  error.status = status;
  return error;
};

const createClientMessageId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `project-chat-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const isImageType = (value) => String(value || "").toLowerCase().startsWith("image/");

const formatFileSize = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const fileFingerprint = (file) =>
  [file?.name, file?.size, file?.lastModified, file?.type]
    .map((part) => String(part || ""))
    .join(":");

const saveBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

function QueuedAttachment({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!isImageType(file?.type)) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="project-chat-queued-file">
      {previewUrl ? (
        <img src={previewUrl} alt="" />
      ) : (
        <Icon aria-hidden="true">insert_drive_file</Icon>
      )}
      <span>
        <strong title={file.name}>{file.name}</strong>
        <small>{formatFileSize(file.size)}</small>
      </span>
      <button type="button" aria-label={"Remove " + file.name} onClick={onRemove}>
        <Icon>close</Icon>
      </button>
    </div>
  );
}

QueuedAttachment.propTypes = {
  file: PropTypes.shape({
    lastModified: PropTypes.number,
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    type: PropTypes.string,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
};

function MessageAttachment({ attachment, fetchFile }) {
  const rootRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const image = Boolean(attachment?.is_image || isImageType(attachment?.mime_type));
  const downloadPath = attachment?.download_path || "";

  useEffect(() => {
    if (!image || !downloadPath) return undefined;
    const controller = new AbortController();
    let observer = null;
    let objectUrl = "";
    let started = false;

    const loadPreview = async () => {
      if (started) return;
      started = true;
      setLoading(true);
      try {
        const blob = await fetchFile(attachment, controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    if (typeof IntersectionObserver === "function" && rootRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer?.disconnect();
            void loadPreview();
          }
        },
        { rootMargin: "100px" }
      );
      observer.observe(rootRef.current);
    } else {
      void loadPreview();
    }

    return () => {
      controller.abort();
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, downloadPath, fetchFile, image]);

  const download = async () => {
    setLoading(true);
    setFailed(false);
    try {
      const blob = await fetchFile(attachment);
      saveBlob(blob, attachment?.name);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (image) {
    return (
      <div className="project-chat-message-image" ref={rootRef}>
        {previewUrl ? (
          <a href={previewUrl} target="_blank" rel="noreferrer" title="Open screenshot">
            <img src={previewUrl} alt={attachment?.name || "Chat screenshot"} />
          </a>
        ) : (
          <button type="button" disabled={loading} onClick={() => void download()}>
            {loading ? <CircularProgress size={16} /> : <Icon>image</Icon>}
            <span>{failed ? "Retry image" : attachment?.name || "Screenshot"}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="project-chat-message-file"
      disabled={loading}
      onClick={() => void download()}
    >
      {loading ? <CircularProgress size={16} /> : <Icon>description</Icon>}
      <span>
        <strong>{failed ? "Retry download" : attachment?.name || "Attachment"}</strong>
        <small>{formatFileSize(attachment?.size)}</small>
      </span>
      <Icon aria-hidden="true">download</Icon>
    </button>
  );
}

MessageAttachment.propTypes = {
  attachment: PropTypes.shape({
    download_path: PropTypes.string,
    is_image: PropTypes.bool,
    mime_type: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
  }).isRequired,
  fetchFile: PropTypes.func.isRequired,
};
export default function ProjectLiveChatDialog({
  open,
  project,
  minimized,
  eventRevision,
  realtimeStatus,
  onClose,
  onMinimize,
  onRestore,
  onUnreadCountChange,
}) {
  const projectId = Number(project?.id);
  const hasProject = Number.isInteger(projectId) && projectId > 0;
  const activeProjectIdRef = useRef(projectId);
  const chatVisibilityRef = useRef({ open, minimized });
  const previousMinimizedRef = useRef(minimized);
  activeProjectIdRef.current = projectId;
  chatVisibilityRef.current = { open, minimized };

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const messagesRef = useRef([]);
  const loadedMessageIdsRef = useRef(new Set());
  const oldestCursorRef = useRef(null);
  const oldestHasMoreRef = useRef(false);
  const gapCursorRef = useRef(null);
  const fetchInFlightRef = useRef(false);
  const pendingLatestRefreshRef = useRef(false);
  const requestVersionRef = useRef(0);
  const composerDraftsRef = useRef(new Map());
  const composerProjectIdRef = useRef(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [retryPayload, setRetryPayload] = useState(null);
  const attachmentInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [mentionValue, setMentionValue] = useState(null);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionOptions, setMentionOptions] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionError, setMentionError] = useState("");
  const [resolvedProject, setResolvedProject] = useState(null);

  const [messageSearch, setMessageSearch] = useState("");
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const messageScrollRef = useRef(null);
  const preserveScrollRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const request = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw createChatRequestError("Your session has ended. Please sign in again.", 401);
    }

    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));

    if (localStorage.getItem("authToken") !== token) {
      throw createChatRequestError("Your session has ended. Please sign in again.", 401);
    }
    if (!response.ok || !payload?.success) {
      throw createChatRequestError(
        messageForResponse(response, payload, "Project chat request failed."),
        response.status
      );
    }

    return payload;
  }, []);

  const fetchAttachment = useCallback(async (attachment, signal) => {
    const token = localStorage.getItem("authToken");
    const path = attachment?.download_path || "";
    if (!token || !path) {
      throw createChatRequestError("Attachment is not available.", token ? 404 : 401);
    }

    const endpoint = /^https?:\/\//i.test(path) ? path : API_URL + path;
    const response = await fetch(endpoint, {
      signal,
      headers: {
        Authorization: "Bearer " + token,
        Accept: "application/octet-stream, application/json",
      },
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw createChatRequestError(
        messageForResponse(response, payload, "Attachment could not be downloaded."),
        response.status
      );
    }

    return response.blob();
  }, []);
  const clearMessageCollection = useCallback(() => {
    messagesRef.current = [];
    loadedMessageIdsRef.current = new Set();
    oldestCursorRef.current = null;
    oldestHasMoreRef.current = false;
    gapCursorRef.current = null;
    setMessages([]);
    setHasMore(false);
  }, []);

  const mergeMessageCollection = useCallback((incoming) => {
    const nextMessages = mergeMessages(messagesRef.current, incoming);
    messagesRef.current = nextMessages;
    loadedMessageIdsRef.current = new Set(nextMessages.map((message) => String(message.id)));
    setMessages(nextMessages);
    return nextMessages;
  }, []);

  const syncHasMore = useCallback(() => {
    setHasMore(Boolean(gapCursorRef.current) || oldestHasMoreRef.current);
  }, []);

  const clearForAccessFailure = useCallback(
    (requestError) => {
      if (![401, 403].includes(Number(requestError?.status))) return false;

      clearMessageCollection();
      setResolvedProject(null);
      setError(requestError.message || "Project chat access is no longer available.");
      onClose();
      return true;
    },
    [clearMessageCollection, onClose]
  );
  const markRead = useCallback(
    async (lastMessageId) => {
      // Background and minimized chats remain unread until the user views them.
      if (
        !hasProject ||
        documentIsHidden() ||
        !chatVisibilityRef.current.open ||
        chatVisibilityRef.current.minimized
      ) {
        return;
      }

      try {
        const payload = await request(`/projects/${projectId}/chat/read`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ last_message_id: lastMessageId || null }),
        });
        onUnreadCountChange(projectId, Number(payload?.unread_count || 0));
      } catch {
        // A read marker is best effort. A later chat refresh can retry it.
      }
    },
    [hasProject, onUnreadCountChange, projectId, request]
  );

  const fetchMessagePage = useCallback(
    async ({ older = false, quiet = false } = {}) => {
      if (
        !hasProject ||
        documentIsHidden() ||
        !chatVisibilityRef.current.open ||
        chatVisibilityRef.current.minimized
      ) {
        return;
      }
      if (fetchInFlightRef.current) {
        if (!older) pendingLatestRefreshRef.current = true;
        return;
      }

      const existingGapCursor = gapCursorRef.current;
      const cursor = older ? existingGapCursor || oldestCursorRef.current : null;
      if (older && !cursor) return;

      const requestVersion = ++requestVersionRef.current;
      fetchInFlightRef.current = true;
      if (older) {
        setLoadingMore(true);
      } else if (!quiet) {
        setLoading(true);
      }

      const isCurrentProjectRequest = () =>
        activeProjectIdRef.current === projectId && requestVersion === requestVersionRef.current;
      const loadPage = async (pageCursor = null) => {
        const query = new URLSearchParams({ limit: "50" });
        if (pageCursor) query.set("cursor", String(pageCursor));
        return request(`/projects/${projectId}/chat/messages?${query.toString()}`);
      };

      try {
        const knownIds = new Set(loadedMessageIdsRef.current);
        const hadLoadedMessages = knownIds.size > 0;
        const payload = await loadPage(cursor);
        if (!isCurrentProjectRequest()) return;

        const normalizedIncoming = mergeMessages(
          Array.isArray(payload.messages) ? payload.messages : []
        );
        const incomingOverlaps = normalizedIncoming.some((message) =>
          knownIds.has(String(message.id))
        );
        normalizedIncoming.forEach((message) => knownIds.add(String(message.id)));
        let collectedMessages = normalizedIncoming;
        let bridgeCursor = null;

        if (older && existingGapCursor) {
          bridgeCursor =
            incomingOverlaps || !payload?.meta?.has_more ? null : payload?.meta?.next_cursor || null;
        } else if (!older && existingGapCursor) {
          bridgeCursor = existingGapCursor;
        } else if (!older && hadLoadedMessages && normalizedIncoming.length && !incomingOverlaps) {
          // More than one newest page may have arrived between refreshes. Page
          // backwards from this page until it touches the loaded history.
          bridgeCursor = normalizedIncoming[0].id;
        }

        let bridgePages = 0;
        while (bridgeCursor && bridgePages < MAX_GAP_PAGES_PER_REFRESH) {
          const bridgePayload = await loadPage(bridgeCursor);
          if (!isCurrentProjectRequest()) return;

          const bridgeMessages = mergeMessages(
            Array.isArray(bridgePayload.messages) ? bridgePayload.messages : []
          );
          if (!bridgeMessages.length) {
            bridgeCursor = null;
            break;
          }

          const bridgeOverlaps = bridgeMessages.some((message) =>
            knownIds.has(String(message.id))
          );
          bridgeMessages.forEach((message) => knownIds.add(String(message.id)));
          collectedMessages = mergeMessages(collectedMessages, bridgeMessages);

          if (bridgeOverlaps || !bridgePayload?.meta?.has_more || !bridgePayload?.meta?.next_cursor) {
            bridgeCursor = null;
            break;
          }

          bridgeCursor = bridgePayload.meta.next_cursor;
          bridgePages += 1;
        }

        gapCursorRef.current = bridgeCursor || null;
        if (!hadLoadedMessages && !older) {
          oldestCursorRef.current = payload?.meta?.next_cursor || null;
          oldestHasMoreRef.current = Boolean(payload?.meta?.has_more);
        } else if (older && !existingGapCursor) {
          oldestCursorRef.current = payload?.meta?.next_cursor || null;
          oldestHasMoreRef.current = Boolean(payload?.meta?.has_more);
        }

        const nextMessages = mergeMessageCollection(collectedMessages);
        syncHasMore();
        setResolvedProject(payload?.project || null);
        setError("");

        // Do not acknowledge through a newest message while a page-sized gap
        // remains. Once the bridge is complete, the marker can safely cover
        // every visible received message in the shared history.
        const bridgeWasResolved = older && existingGapCursor && !gapCursorRef.current;
        if (
          !gapCursorRef.current &&
          ((!older && normalizedIncoming.length) || bridgeWasResolved) &&
          nextMessages.length
        ) {
          void markRead(nextMessages[nextMessages.length - 1]?.id);
        }
      } catch (requestError) {
        if (!isCurrentProjectRequest()) return;
        if (older) preserveScrollRef.current = null;
        if (clearForAccessFailure(requestError)) return;
        if (!quiet) {
          setError(requestError?.message || "Project messages could not be loaded.");
        }
      } finally {
        // An event arriving during a history request must not be lost just
        // because that request was already in flight.
        if (requestVersion === requestVersionRef.current) {
          fetchInFlightRef.current = false;
          if (activeProjectIdRef.current === projectId) {
            setLoading(false);
            setLoadingMore(false);
            if (
              pendingLatestRefreshRef.current &&
              chatVisibilityRef.current.open &&
              !chatVisibilityRef.current.minimized
            ) {
              pendingLatestRefreshRef.current = false;
              void fetchMessagePage({ quiet: true });
            }
          }
        }
      }
    },
    [
      clearForAccessFailure,
      hasProject,
      markRead,
      mergeMessageCollection,
      projectId,
      request,
      syncHasMore,
    ]
  );

  // Persist draft state in memory for each project. A chat switch can change the
  // visible project, but it must never discard the message the user was typing.
  useEffect(() => {
    const composerProjectId = composerProjectIdRef.current;
    if (!composerProjectId || composerProjectId !== projectId) return;

    composerDraftsRef.current.set(String(composerProjectId), {
      draft,
      attachments,
      mentionValue,
      mentionSearch,
      retryPayload,
    });
  }, [attachments, draft, mentionSearch, mentionValue, projectId, retryPayload]);

  useEffect(() => {
    const nextProjectId = hasProject ? projectId : null;
    const previousProjectId = composerProjectIdRef.current;
    if (previousProjectId === nextProjectId) return;

    if (previousProjectId) {
      composerDraftsRef.current.set(String(previousProjectId), {
        draft,
        attachments,
        mentionValue,
        mentionSearch,
        retryPayload,
      });
    }

    composerProjectIdRef.current = nextProjectId;
    const savedDraft = nextProjectId
      ? composerDraftsRef.current.get(String(nextProjectId))
      : null;
    setDraft(savedDraft?.draft || "");
    setAttachments(savedDraft?.attachments || []);
    setMentionValue(savedDraft?.mentionValue || null);
    setMentionSearch(savedDraft?.mentionSearch || "");
    setRetryPayload(savedDraft?.retryPayload || null);
    setMentionOptions([]);
  }, [
    attachments,
    draft,
    hasProject,
    mentionSearch,
    mentionValue,
    projectId,
    retryPayload,
  ]);
  useEffect(() => {
    requestVersionRef.current += 1;
    fetchInFlightRef.current = false;
    pendingLatestRefreshRef.current = false;
    preserveScrollRef.current = null;
    stickToBottomRef.current = true;
    setMessageSearch("");
    setShowMentionPicker(false);
    clearMessageCollection();
    setError("");
    setResolvedProject(null);

    if (hasProject && !chatVisibilityRef.current.minimized) {
      void fetchMessagePage();
    }
  }, [clearMessageCollection, fetchMessagePage, hasProject, projectId]);


  useEffect(() => {
    const wasMinimized = previousMinimizedRef.current;
    previousMinimizedRef.current = minimized;
    if (open && hasProject && wasMinimized && !minimized) {
      void fetchMessagePage({ quiet: true });
    }
  }, [fetchMessagePage, hasProject, minimized, open]);
  useEffect(() => {
    if (!open || minimized || !hasProject) return undefined;

    const refreshWhenVisible = () => {
      if (documentIsHidden()) return;
      void fetchMessagePage({ quiet: true });
    };

    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
    };
  }, [fetchMessagePage, hasProject, minimized, open]);

  useEffect(() => {
    if (open && !minimized && hasProject && eventRevision > 0) {
      void fetchMessagePage({ quiet: true });
    }
  }, [eventRevision, fetchMessagePage, hasProject, minimized, open]);

  useEffect(() => {
    if (!hasProject || !showMentionPicker || mentionSearch.trim().length < 2) {
      setMentionOptions([]);
      setMentionLoading(false);
      setMentionError("");
      return undefined;
    }

    const controller = new AbortController();
    setMentionOptions([]);
    setMentionLoading(true);
    setMentionError("");
    const timer = window.setTimeout(async () => {
      try {
        const query = new URLSearchParams({ q: mentionSearch.trim(), limit: "20" });
        const payload = await request(
          "/projects/" + projectId + "/chat/users?" + query.toString(),
          { signal: controller.signal }
        );
        if (!controller.signal.aborted) {
          setMentionOptions(Array.isArray(payload.users) ? payload.users : []);
        }
      } catch (searchError) {
        if (!controller.signal.aborted) {
          setMentionOptions([]);
          setMentionError(searchError?.message || "Could not search teammates. Try again.");
        }
      } finally {
        if (!controller.signal.aborted) setMentionLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [hasProject, mentionSearch, projectId, request, showMentionPicker]);
  const addAttachments = useCallback((files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) return;

    const oversizedFile = selectedFiles.find((file) => Number(file.size) > MAX_ATTACHMENT_SIZE);
    if (oversizedFile) {
      setError(oversizedFile.name + " exceeds the 10 MB attachment limit.");
      return;
    }

    setAttachments((current) => {
      const next = [...current];
      selectedFiles.forEach((file) => {
        const duplicate = next.some((item) => fileFingerprint(item) === fileFingerprint(file));
        if (!duplicate && next.length < MAX_ATTACHMENTS) next.push(file);
      });
      if (next.length < current.length + selectedFiles.length) {
        setError("You can attach up to " + MAX_ATTACHMENTS + " unique files.");
      } else {
        setError("");
      }
      return next;
    });
  }, []);

  const removeAttachment = useCallback((fileIndex) => {
    setAttachments((current) => current.filter((file, index) => index !== fileIndex));
    setRetryPayload(null);
  }, []);

  const handleComposerPaste = (event) => {
    const pastedImages = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === "file" && isImageType(item.type))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (pastedImages.length) addAttachments(pastedImages);
  };

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body && attachments.length === 0) {
      setError("Enter a message or attach a file before sending.");
      return;
    }
    if (body.length > MAX_BODY_LENGTH) {
      setError("A project chat message can contain at most " + MAX_BODY_LENGTH + " characters.");
      return;
    }

    const mentionId = Number(mentionValue?.id);
    const mentionedUserId = Number.isInteger(mentionId) && mentionId > 0 ? mentionId : null;
    const queuedFiles = [...attachments];
    const attachmentSignature = queuedFiles.map(fileFingerprint).join("|");
    const canRetrySameRequest =
      retryPayload &&
      retryPayload.body === body &&
      (retryPayload.mentioned_user_id || null) === mentionedUserId &&
      retryPayload.attachment_signature === attachmentSignature;
    const payload = canRetrySameRequest
      ? retryPayload
      : {
          body,
          mentioned_user_id: mentionedUserId,
          client_message_id: createClientMessageId(),
          attachments: queuedFiles,
          attachment_signature: attachmentSignature,
        };

    setSending(true);
    setError("");
    setRetryPayload(payload);

    try {
      const formData = new FormData();
      if (payload.body) formData.append("body", payload.body);
      if (payload.mentioned_user_id) {
        formData.append("mentioned_user_id", String(payload.mentioned_user_id));
      }
      formData.append("client_message_id", payload.client_message_id);
      payload.attachments.forEach((file) => formData.append("attachments[]", file));

      const response = await request("/projects/" + projectId + "/chat/messages", {
        method: "POST",
        body: formData,
      });
      const sentMessage = response?.message;
      if (activeProjectIdRef.current !== projectId) return;
      if (sentMessage?.id !== undefined && sentMessage?.id !== null) {
        stickToBottomRef.current = true;
        setMessageSearch("");
        setResolvedProject((current) => current || project);
        mergeMessageCollection([sentMessage]);
      }
      setDraft("");
      setAttachments([]);
      setMentionValue(null);
      setMentionSearch("");
      setMentionOptions([]);
      setRetryPayload(null);
      setShowMentionPicker(false);
    } catch (requestError) {
      setError(requestError?.message || "Message could not be sent. You can retry safely.");
    } finally {
      setSending(false);
    }
  };
  const titleProject = resolvedProject || project;
  const titleCode = titleProject?.quest_code || titleProject?.project_code || String(projectId);
  const titleName = titleProject?.project_title || titleProject?.project_name || "Project conversation";
  // Online describes this user's open chat window, not another user's presence.
  // Reverb connection health stays visible separately when live sync is down.
  const liveSyncWarning =
    realtimeStatus === "connected"
      ? ""
      : realtimeStatus === "connecting"
        ? "Connecting live alerts…"
        : "Live alerts unavailable. Use refresh to check new messages.";
  const searchTerm = messageSearch.trim().toLocaleLowerCase();
  const projectMatchesLoaded = Number(resolvedProject?.id) === projectId;
  const visibleMessages = useMemo(
    () =>
      (projectMatchesLoaded ? messages : []).filter((message) => {
        if (!searchTerm) return true;
        return (
          safeText(message?.body).toLocaleLowerCase().includes(searchTerm) ||
          safeText(message?.sender?.name).toLocaleLowerCase().includes(searchTerm) ||
          (message?.attachments || []).some((attachment) =>
            safeText(attachment?.name).toLocaleLowerCase().includes(searchTerm)
          )
        );
      }),
    [messages, projectMatchesLoaded, searchTerm]
  );

  const handleMessageScroll = () => {
    const viewport = messageScrollRef.current;
    if (!viewport) return;
    stickToBottomRef.current =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 72;
  };

  const loadOlderMessages = () => {
    const viewport = messageScrollRef.current;
    if (viewport && !fetchInFlightRef.current) {
      preserveScrollRef.current = {
        height: viewport.scrollHeight,
        top: viewport.scrollTop,
      };
    }
    void fetchMessagePage({ older: true });
  };

  const handleComposerKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if ((draft.trim() || attachments.length) && !sending && !loading) void sendMessage();
  };

  useLayoutEffect(() => {
    if (!open || minimized || !hasProject) return;
    const viewport = messageScrollRef.current;
    if (!viewport) return;

    if (preserveScrollRef.current) {
      viewport.scrollTop =
        preserveScrollRef.current.top +
        viewport.scrollHeight -
        preserveScrollRef.current.height;
      preserveScrollRef.current = null;
    } else if (stickToBottomRef.current && !searchTerm) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  }, [hasProject, messages, minimized, open, projectId, searchTerm]);

  if (!open || !hasProject) return null;

  return (
    <Paper
      className={"project-chat-panel" + (minimized ? " minimized" : "")}
      elevation={0}
      role="region"
      aria-label={"Project " + titleCode + " live chat"}
    >
      <header className="project-chat-header">
        <div className="project-chat-header-icon" aria-hidden="true">
          <Icon>forum</Icon>
        </div>
        <div className="project-chat-header-copy">
          <h2 className="project-chat-title">{titleCode}</h2>
          <div className="project-chat-subtitle">
            <span className="project-chat-status-dot live" title="Chat window open" />
            <span>{titleName} · Online</span>
          </div>
        </div>
        <button
          type="button"
          className="project-chat-icon-button"
          aria-label={minimized ? "Restore project chat" : "Minimize project chat"}
          title={minimized ? "Restore chat" : "Minimize chat"}
          onClick={minimized ? onRestore : onMinimize}
        >
          <Icon>{minimized ? "open_in_full" : "remove"}</Icon>
        </button>
        <button
          type="button"
          className="project-chat-icon-button"
          aria-label="Close project chat"
          title="Close chat"
          onClick={onClose}
        >
          <Icon>close</Icon>
        </button>
      </header>

      {!minimized && (
        <>
      <div className="project-chat-thread-toolbar">
        <Icon fontSize="small" aria-hidden="true">people_outline</Icon>
        <span className="project-chat-thread-meta">Shared history for this project code</span>
        <span className="project-chat-thread-count">
          {projectMatchesLoaded ? messages.length : 0} loaded
        </span>
        <button
          type="button"
          className="project-chat-icon-button"
          aria-label="Refresh project messages"
          title="Refresh messages"
          onClick={() => void fetchMessagePage()}
        >
          <Icon fontSize="small">refresh</Icon>
        </button>
      </div>

      {liveSyncWarning && (
        <div className="project-chat-sync-notice" role="status">
          <Icon fontSize="inherit" aria-hidden="true">
            {realtimeStatus === "connecting" ? "sync" : "wifi_off"}
          </Icon>
          <span>{liveSyncWarning}</span>
        </div>
      )}

      <label className="project-chat-message-search">
        <Icon fontSize="small" aria-hidden="true">search</Icon>
        <input
          className="project-chat-search-input"
          type="search"
          value={messageSearch}
          onChange={(event) => setMessageSearch(event.target.value)}
          placeholder="Search loaded messages"
          aria-label="Search loaded project messages"
        />
      </label>

      <div
        className="project-chat-messages"
        ref={messageScrollRef}
        onScroll={handleMessageScroll}
        role="log"
        aria-label="Project chat history"
        aria-live="polite"
      >
        {hasMore && projectMatchesLoaded && (
          <button
            type="button"
            className="project-chat-history-button"
            disabled={loadingMore || loading}
            onClick={loadOlderMessages}
          >
            {loadingMore ? "Loading history…" : "Load older messages"}
          </button>
        )}

        {loading && !projectMatchesLoaded ? (
          <div className="project-chat-empty">
            <CircularProgress size={23} />
            <span>Loading conversation…</span>
          </div>
        ) : visibleMessages.length ? (
          visibleMessages.map((message, index) => {
            const previousMessage = visibleMessages[index - 1];
            const showDay =
              !previousMessage ||
              new Date(previousMessage.created_at).toDateString() !==
                new Date(message.created_at).toDateString();
            const ownMessage = Boolean(message?.is_own);
            const senderName = ownMessage ? "You" : message?.sender?.name || "User";

            return (
              <React.Fragment key={message.id}>
                {showDay && <div className="project-chat-day">{formatDay(message.created_at)}</div>}
                <div className={"project-chat-message-row" + (ownMessage ? " own" : "")}>
                  {!ownMessage && (
                    <div className="project-chat-avatar" aria-hidden="true">
                      {initials(senderName)}
                    </div>
                  )}
                  <div className="project-chat-message-content">
                    {!ownMessage && <span className="project-chat-sender">{senderName}</span>}
                    {safeText(message.body) && (
                      <div className="project-chat-bubble">{safeText(message.body)}</div>
                    )}
                    {Array.isArray(message?.attachments) && message.attachments.length > 0 && (
                      <div className="project-chat-message-attachments">
                        {message.attachments.map((attachment) => (
                          <MessageAttachment
                            key={attachment.id}
                            attachment={attachment}
                            fetchFile={fetchAttachment}
                          />
                        ))}
                      </div>
                    )}
                    {message?.mentioned_user && (
                      <span className="project-chat-message-mention">
                        @{message.mentioned_user.name || message.mentioned_user.email}
                      </span>
                    )}
                    <time
                      className="project-chat-message-time"
                      dateTime={message.created_at || undefined}
                      title={formatDateTime(message.created_at)}
                    >
                      {formatTime(message.created_at)}
                    </time>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        ) : (
          <div className="project-chat-empty">
            <Icon aria-hidden="true">chat_bubble_outline</Icon>
            <span>
              {searchTerm
                ? "No match in loaded messages. Load older messages to search more history."
                : "No messages yet. Start this project conversation."}
            </span>
          </div>
        )}
      </div>

      <div className="project-chat-composer">
        {error && <Alert severity="error" className="project-chat-alert">{error}</Alert>}
        {mentionValue && (
          <div className="project-chat-mention-selected">
            <Icon fontSize="small" aria-hidden="true">alternate_email</Icon>
            <span>Auto-open for {mentionValue.name || mentionValue.email}</span>
            <button
              type="button"
              aria-label="Remove selected teammate"
              onClick={() => {
                setMentionValue(null);
                setMentionSearch("");
              }}
            >
              <Icon fontSize="small">close</Icon>
            </button>
          </div>
        )}
        {showMentionPicker && (
          <div className="project-chat-mention-area">
            <div className="project-chat-mention-heading">
              <Icon fontSize="small" aria-hidden="true">alternate_email</Icon>
              <span>Notify teammate</span>
            </div>
            <Autocomplete
              className="project-chat-mention-autocomplete"
              size="small"
              value={mentionValue}
              inputValue={mentionSearch}
              options={mentionOptions}
              loading={mentionLoading}
              loadingText="Searching teammates…"
              noOptionsText={
                mentionError ||
                (mentionSearch.trim().length < 2
                  ? "Type at least 2 letters to search"
                  : "No eligible teammate found")
              }
              openOnFocus
              popupIcon={null}
              PopperComponent={MentionPopper}
              slotProps={{ paper: { className: "project-chat-mention-paper" } }}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              getOptionLabel={(option) => option?.name || option?.email || ""}
              onChange={(_, value) => {
                setMentionValue(value || null);
                if (value) setShowMentionPicker(false);
              }}
              onInputChange={(_, value) => setMentionSearch(value || "")}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span className="project-chat-mention-avatar" aria-hidden="true">
                    {initials(option.name || option.email)}
                  </span>
                  <span className="project-chat-mention-option-copy">
                    <strong>{option.name || "User"}</strong>
                    <small>{option.email || ""}</small>
                  </span>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="project-chat-mention-field"
                  size="small"
                  placeholder="Search teammate (2+ letters)"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Icon className="project-chat-mention-search-icon" aria-hidden="true">
                          search
                        </Icon>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {mentionLoading && <CircularProgress color="inherit" size={15} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <div className="project-chat-mention-helper">
              Chat opens automatically only for the selected teammate. History stays shared.
            </div>
          </div>
        )}
        {attachments.length > 0 && (
          <div className="project-chat-queued-files" aria-label="Selected attachments">
            {attachments.map((file, fileIndex) => (
              <QueuedAttachment
                key={fileFingerprint(file)}
                file={file}
                onRemove={() => removeAttachment(fileIndex)}
              />
            ))}
          </div>
        )}
        <input
          ref={imageInputRef}
          className="project-chat-file-input"
          type="file"
          accept="image/*"
          multiple
          tabIndex={-1}
          onChange={(event) => {
            addAttachments(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={attachmentInputRef}
          className="project-chat-file-input"
          type="file"
          multiple
          tabIndex={-1}
          onChange={(event) => {
            addAttachments(event.target.files);
            event.target.value = "";
          }}
        />
        <div className="project-chat-compose-row">
          <button
            type="button"
            className={
              "project-chat-icon-button project-chat-mention-toggle" +
              (showMentionPicker ? " selected" : "")
            }
            aria-label={showMentionPicker ? "Hide teammate picker" : "Notify a teammate"}
            title="Notify a teammate"
            onClick={() => setShowMentionPicker((shown) => !shown)}
          >
            <Icon>alternate_email</Icon>
          </button>
          <button
            type="button"
            className="project-chat-icon-button project-chat-compose-action"
            aria-label="Add screenshot or image"
            title="Add screenshot or image"
            disabled={attachments.length >= MAX_ATTACHMENTS || sending}
            onClick={() => imageInputRef.current?.click()}
          >
            <Icon>image</Icon>
          </button>
          <button
            type="button"
            className="project-chat-icon-button project-chat-compose-action"
            aria-label="Attach files"
            title="Attach files"
            disabled={attachments.length >= MAX_ATTACHMENTS || sending}
            onClick={() => attachmentInputRef.current?.click()}
          >
            <Icon>attach_file</Icon>
          </button>
          <textarea
            className="project-chat-compose-input"
            rows={2}
            maxLength={MAX_BODY_LENGTH}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            onPaste={handleComposerPaste}
            aria-label="Message"
            placeholder="Write a message…"
          />
          <button
            type="button"
            className="project-chat-send"
            aria-label="Send message"
            title="Send message"
            disabled={(!draft.trim() && attachments.length === 0) || sending || loading}
            onClick={() => void sendMessage()}
          >
            {sending ? <CircularProgress color="inherit" size={17} /> : <Icon>send</Icon>}
          </button>
        </div>
        <div className="project-chat-composer-hint">
          <span>Paste screenshots · up to {MAX_ATTACHMENTS} files, 10 MB each</span>
          <span>{draft.length}/{MAX_BODY_LENGTH}</span>
        </div>
      </div>
        </>
      )}
    </Paper>
  );
}
ProjectLiveChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    quest_code: PropTypes.string,
    project_title: PropTypes.string,
  }),

  minimized: PropTypes.bool.isRequired,
  eventRevision: PropTypes.number.isRequired,
  realtimeStatus: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onUnreadCountChange: PropTypes.func.isRequired,
};
